/**
 * Manage-Booking Link Service
 *
 * Two operator actions tied to the customer-facing manage-booking app
 * (https://manage.store-rentals.com/b/<token>), which offers payment via both
 * Stripe and PayPal:
 *
 *  - sendPaymentEmail: triggers the same Make.com customer email as the
 *    "payment link" option in the new-booking flow (payment_flow: 'link'). It
 *    emails the customer their manage/payment link. Routed through the open
 *    booking-webhook endpoint, exactly like the other Emails-tab sends.
 *
 *  - generateManageLink: mints a fresh booking token server-side and returns
 *    the manage URL for the operator to copy and share themselves (email / SMS
 *    / WhatsApp). Authenticated — the raw token is a bearer credential, so the
 *    caller's Supabase JWT is sent and verified server-side.
 */

import { supabase } from '../lib/supabase';

const BOOKING_WEBHOOK_URL = '/api/booking-webhook';
const GENERATE_MANAGE_LINK_URL = '/api/generate-manage-link';

export interface SendPaymentEmailParams {
  bookingId: string;
  endDate: string;
  bookingState: string;
  paymentState: string;
  language: string;
}

/**
 * Send the customer a payment email pointing at the manage-booking app.
 * Reuses the new-booking "payment link" Make.com flow (payment_flow: 'link'),
 * forcing send_now so the email goes out immediately.
 */
export async function sendPaymentEmail(params: SendPaymentEmailParams) {
  try {
    const response = await fetch(BOOKING_WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        booking_id: params.bookingId,
        end_date: params.endDate,
        send_now: true,
        booking_state: params.bookingState,
        payment_state: params.paymentState,
        payment_flow: 'link',
        language: params.language,
      }),
    });

    const result = await response.json().catch(() => ({}));
    if (!response.ok || result.success === false) {
      return { data: null, error: result.message || 'Failed to send payment email' };
    }
    return { data: { sent: true }, error: null };
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'Network error' };
  }
}

export interface GenerateManageLinkResult {
  url: string;
  token: string;
  expiresAt: string;
}

/**
 * Mint a fresh manage-booking link for the operator to copy. Authenticated.
 */
export async function generateManageLink(params: { bookingId: string }) {
  try {
    const { data: sessionResult } = await supabase.auth.getSession();
    const token = sessionResult.session?.access_token;
    if (!token) {
      return { data: null, error: 'Not signed in' };
    }

    const response = await fetch(GENERATE_MANAGE_LINK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ bookingId: params.bookingId }),
    });

    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result.success) {
      return { data: null, error: result.message || 'Failed to generate link' };
    }
    return {
      data: {
        url: result.url as string,
        token: result.token as string,
        expiresAt: result.expiresAt as string,
      } as GenerateManageLinkResult,
      error: null,
    };
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'Network error' };
  }
}
